import React from "react";
import "./whats-new.css";

// "New since your last visit" divider (design 1a). Separates the unread block
// from already-seen posts; placement is driven by the last-visit marker.
// Desktop margins 6px 2px 2px / mobile sits on the stack gap (1f: 14px 2px).
export const UnreadDivider = () => (
  <div
    className="flex items-center gap-2.5 mx-0.5 sm:mt-1.5 sm:mb-0.5"
    data-testid="unread-divider"
  >
    <span className="flex-1 h-px bg-[#C9DFFB]" />
    <span className="text-[11px] font-semibold text-[#4B8DE8] tracking-[0.01em]">
      New since your last visit
    </span>
    <span className="flex-1 h-px bg-[#C9DFFB]" />
  </div>
);

// "EARLIER UPDATES" divider (design 1a) — closes the unread block; everything
// below it was already seen on a previous visit. Pure presentation derived
// from the same last-visit marker as the unread divider.
export const EarlierDivider = () => (
  <div
    className="flex items-center gap-3 sm:mt-2.5 sm:mb-0.5"
    data-testid="earlier-divider"
  >
    <span className="flex-1 h-px bg-[#DDE4EC]" />
    <span className="text-[10.5px] font-extrabold tracking-[0.1em] text-[#93A1B4]">
      EARLIER UPDATES
    </span>
    <span className="flex-1 h-px bg-[#DDE4EC]" />
  </div>
);

// Empty state (design 1e — icon S18, ring in a circle).
export const EmptyState = () => (
  <div className="bg-white border border-[#E7EBF1] rounded-2xl shadow-[0_1px_2px_rgba(16,27,44,0.04)] py-[46px] px-6 flex flex-col items-center text-center">
    <span className="w-[52px] h-[52px] rounded-full bg-[#EAF3FF] flex items-center justify-center">
      <span className="w-4 h-4 rounded-full border-[3px] border-[#2283FF] box-border" />
    </span>
    <span className="mt-4 text-[16px] font-bold text-[#101B2C]">Nothing new yet</span>
    <span className="mt-1.5 text-[13.5px] leading-[1.55] text-[#7C8CA1] max-w-[260px]">
      Updates from the TransferPitch team will appear here as they ship.
    </span>
  </div>
);

// Loading skeleton cards (design 1e — shimmer S20, exact bar geometry).
const Bar = ({ className }) => <div className={`tp-shimmer ${className}`} />;

export const PostSkeleton = ({ withImage = false }) => (
  <div className="bg-white border border-[#E7EBF1] rounded-2xl p-[22px]">
    {withImage ? (
      <>
        <Bar className="h-5 w-[120px] rounded-full" />
        <Bar className="h-[120px] w-full mt-[15px] rounded-[10px]" />
        <Bar className="h-4 w-[64%] mt-[15px] rounded-md" />
      </>
    ) : (
      <>
        <Bar className="h-5 w-24 rounded-full" />
        <Bar className="h-4 w-[72%] mt-[15px] rounded-md" />
        <Bar className="h-3 w-full mt-[13px] rounded-md" />
        <Bar className="h-3 w-[86%] mt-2 rounded-md" />
        <div className="flex gap-2 mt-[17px]">
          {[0, 1, 2].map((i) => (
            <Bar key={i} className="h-[30px] w-[58px] rounded-full" />
          ))}
        </div>
      </>
    )}
  </div>
);

export const FeedSkeleton = () => (
  <div className="flex flex-col gap-3.5" data-testid="feed-skeleton">
    <PostSkeleton />
    <PostSkeleton withImage />
  </div>
);

// Load-more button + its loading variant (design 1a / 1e, spinner S19).
export const LoadMoreButton = ({ loading, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className={[
      "mx-auto mt-2 flex items-center justify-center gap-[9px] h-10 px-[22px] rounded-full border border-[#D8E0EA] bg-white text-[13px] font-bold",
      loading
        ? "text-[#93A1B4] cursor-default"
        : "text-[#33455E] hover:border-[#8FBEFC] hover:bg-[#F7FAFF]",
    ].join(" ")}
  >
    {loading && (
      <span className="tp-spin w-3.5 h-3.5 rounded-full border-2 border-[#C7D6E8] border-t-[#2283FF] box-border" />
    )}
    {loading ? "Loading earlier updates…" : "Load earlier updates"}
  </button>
);
